import { useTranslation } from 'react-i18next';
import { Button } from '@xxtract/ui';
import { marketName } from '../review.utils';

export interface SubmitBarProps {
  unreviewedCount: number;
  hasValidationErrors: boolean;
  /**
   * h13-5: de doelmarkten waarvoor nog geen afnemer is gekozen. De knop draagt al waarom hij niet
   * kan; deze reden voegt zich daarbij in plaats van er een rood kader naast te zetten.
   */
  marketsWithoutRetailer?: string[];
  /** Hoeveel doelmarkten deze aanmelding kent — bepaalt of een landnaam iets toevoegt. */
  marketCount?: number;
  disabled?: boolean;
  onSubmit: () => void;
}

/** FR31/FR33: the submit button stays disabled until every column is reviewed and valid. */
export function SubmitBar({
  unreviewedCount,
  hasValidationErrors,
  marketsWithoutRetailer = [],
  marketCount = 0,
  disabled,
  onSubmit,
}: SubmitBarProps) {
  const { t } = useTranslation();
  const isReady = unreviewedCount === 0 && !hasValidationErrors && marketsWithoutRetailer.length === 0;

  return (
    <div className="flex items-center justify-between gap-3 border-t border-[var(--border)] pt-4">
      <p role="status" aria-live="polite" data-testid="intake-submit-counter" className="text-sm text-[var(--foreground-muted)]">
        {isReady ? t('intake.review.submitBar.ready') : reden()}
      </p>
      <Button data-testid="intake-submit-button" disabled={disabled || !isReady} onClick={onSubmit}>
        {t('intake.review.submitBar.cta')}
      </Button>
    </div>
  );

  function reden(): string {
    // Ongecontroleerde kolommen eerst: dat is het werk waar de klant middenin zit, en de
    // afnemerskeuze is de afronding daarvan.
    if (unreviewedCount > 0 || hasValidationErrors) return t('intake.review.submitBar.remaining', { count: unreviewedCount });
    // Is er nergens iets gekozen, dan helpt een landnaam niet — er is nog niets te onderscheiden.
    // Ontbreekt er één land tussen gevulde landen, dan hoort dát land erbij: anders moet de klant
    // zelf gaan zoeken welke regel leeg is.
    if (marketsWithoutRetailer.length >= marketCount) return t('intake.review.submitBar.blockedByRetailers');
    return t('intake.review.submitBar.blockedByMarket', { market: marketName(t, marketsWithoutRetailer[0]!) });
  }
}
